import { getMatchGroup, getOptionalMatchGroup, stripLabelQuotes } from './flowchart-parser-labels';
import { FlowchartLabelOrigin, FlowchartNodeShape } from './flowchart-parser-model';

interface NodeShapePattern {
  shape: FlowchartNodeShape;
  regex: RegExp;
}

export interface ParsedNodeReference {
  id: string;
  label: string | null;
  labelOrigin: FlowchartLabelOrigin;
  shape: FlowchartNodeShape | null;
  classNames: string[];
}

interface InlineClassResult {
  expression: string;
  classNames: string[];
}

interface ExtendedShapeDescriptorParts {
  shapeBody: string;
  remainingDescriptor: string;
}

interface NodeExpressionParts {
  id: string;
  descriptor: string;
  classNames: string[];
}

interface NodeReferenceBuildInput {
  id: string;
  label: string | null;
  labelOrigin: FlowchartLabelOrigin;
  shape: FlowchartNodeShape | null;
  classNames: string[];
}

interface NodeShapePatternMatch {
  pattern: NodeShapePattern;
  match: RegExpMatchArray;
}

const NODE_ID_REGEX = /^([A-Za-z0-9_][A-Za-z0-9_-]*)([\s\S]*)$/;
const INLINE_CLASS_SELECTOR_REGEX = /:::\s*([A-Za-z0-9_-]+)\s*$/;
const EXTENDED_SHAPE_REGEX = /^@\{\s*([^}]*)\}\s*([\s\S]*)$/;
const EXTENDED_SHAPE_NAME_REGEX = /\bshape\s*:\s*([A-Za-z0-9_-]+)/i;
const EXTENDED_LABEL_REGEX = /\blabel\s*:\s*("[^"]*"|[^,}]+)/i;

const NODE_SHAPE_PATTERNS: ReadonlyArray<NodeShapePattern> = [
  { shape: FlowchartNodeShape.Terminator, regex: /^\(\[([\s\S]+)\]\)$/ },
  { shape: FlowchartNodeShape.Database, regex: /^\[\(([\s\S]+)\)\]$/ },
  { shape: FlowchartNodeShape.PredefinedProcess, regex: /^\[\[([\s\S]+)\]\]$/ },
  { shape: FlowchartNodeShape.Connector, regex: /^\(\(([\s\S]+)\)\)$/ },
  { shape: FlowchartNodeShape.Hexagon, regex: /^\{\{([\s\S]+)\}\}$/ },
  { shape: FlowchartNodeShape.Trapezoid, regex: /^\[[/\\]([\s\S]+)[/\\]\]$/ },
  { shape: FlowchartNodeShape.InputOutput, regex: /^\[\/([\s\S]+)\/\]$/ },
  { shape: FlowchartNodeShape.InputOutput, regex: /^\[\\([\s\S]+)\\\]$/ },
  { shape: FlowchartNodeShape.Terminator, regex: /^\(([\s\S]+)\)$/ },
  { shape: FlowchartNodeShape.Decision, regex: /^\{([\s\S]+)\}$/ },
  { shape: FlowchartNodeShape.Process, regex: /^\[([\s\S]+)\]$/ },
];

const EXTENDED_SHAPE_BY_NAME: Record<string, FlowchartNodeShape> = {
  rect: FlowchartNodeShape.Process,
  rectangle: FlowchartNodeShape.Process,
  process: FlowchartNodeShape.Process,
  rounded: FlowchartNodeShape.Terminator,
  stadium: FlowchartNodeShape.Terminator,
  terminator: FlowchartNodeShape.Terminator,
  diamond: FlowchartNodeShape.Decision,
  decision: FlowchartNodeShape.Decision,
  question: FlowchartNodeShape.Decision,
  io: FlowchartNodeShape.InputOutput,
  input: FlowchartNodeShape.InputOutput,
  output: FlowchartNodeShape.InputOutput,
  inputOutput: FlowchartNodeShape.InputOutput,
  circle: FlowchartNodeShape.Connector,
  connector: FlowchartNodeShape.Connector,
  doc: FlowchartNodeShape.Document,
  document: FlowchartNodeShape.Document,
  cyl: FlowchartNodeShape.Database,
  cylinder: FlowchartNodeShape.Database,
  database: FlowchartNodeShape.Database,
  db: FlowchartNodeShape.Database,
  subroutine: FlowchartNodeShape.PredefinedProcess,
  predefined: FlowchartNodeShape.PredefinedProcess,
  predefinedProcess: FlowchartNodeShape.PredefinedProcess,
  hex: FlowchartNodeShape.Hexagon,
  hexagon: FlowchartNodeShape.Hexagon,
  prep: FlowchartNodeShape.Hexagon,
  preparation: FlowchartNodeShape.Hexagon,
  trap: FlowchartNodeShape.Trapezoid,
  trapezoid: FlowchartNodeShape.Trapezoid,
};

function removeInlineClassSelectors(expression: string): InlineClassResult {
  let nextExpression = expression.trim();
  const classNames: string[] = [];
  let classMatch = nextExpression.match(INLINE_CLASS_SELECTOR_REGEX);
  while (classMatch && classMatch.index != null) {
    const className = classMatch[1];
    if (className) {
      classNames.unshift(className);
    }
    nextExpression = nextExpression.slice(0, classMatch.index).trimEnd();
    classMatch = nextExpression.match(INLINE_CLASS_SELECTOR_REGEX);
  }
  return { expression: nextExpression, classNames };
}

function readExtendedShapeDescriptorParts(match: RegExpMatchArray): ExtendedShapeDescriptorParts {
  return {
    shapeBody: getMatchGroup({ match, index: 1 }) ?? '',
    remainingDescriptor: (getMatchGroup({ match, index: 2 }) ?? '').trim(),
  };
}

function readExtendedShape(shapeBody: string): FlowchartNodeShape {
  const shapeName = getOptionalMatchGroup({
    match: shapeBody.match(EXTENDED_SHAPE_NAME_REGEX),
    index: 1,
  });
  return EXTENDED_SHAPE_BY_NAME[shapeName?.trim() ?? ''] ?? FlowchartNodeShape.Process;
}

function toNullableLabel(raw: string | null): string | null {
  if (!raw) {
    return null;
  }
  const label = stripLabelQuotes(raw);
  return label.length > 0 ? label : null;
}

function readExtendedShapeLabel(parts: ExtendedShapeDescriptorParts): string | null {
  const objectLabel = getOptionalMatchGroup({
    match: parts.shapeBody.match(EXTENDED_LABEL_REGEX),
    index: 1,
  });
  return toNullableLabel(objectLabel || parts.remainingDescriptor);
}

function parseExtendedShapeDescriptor(
  descriptor: string,
): { shape: FlowchartNodeShape; label: string | null } | null {
  const match = descriptor.match(EXTENDED_SHAPE_REGEX);
  if (!match) {
    return null;
  }

  const parts = readExtendedShapeDescriptorParts(match);
  return {
    shape: readExtendedShape(parts.shapeBody),
    label: readExtendedShapeLabel(parts),
  };
}

function buildNodeReference(input: NodeReferenceBuildInput): ParsedNodeReference {
  return {
    id: input.id,
    label: input.label,
    labelOrigin: input.labelOrigin,
    shape: input.shape,
    classNames: input.classNames,
  };
}

function readNodeExpressionParts(input: {
  expression: string;
}): NodeExpressionParts | null {
  const inlineClasses = removeInlineClassSelectors(input.expression);
  const trimmed = inlineClasses.expression.trim();
  if (!trimmed) {
    return null;
  }

  const idMatch = trimmed.match(NODE_ID_REGEX);
  if (!idMatch) {
    return null;
  }

  const id = idMatch[1] ?? '';
  const descriptor = (idMatch[2] ?? '').trim();
  return { id, descriptor, classNames: inlineClasses.classNames };
}

function labelOriginForLabel(input: {
  label: string | null;
  fallbackOrigin: FlowchartLabelOrigin.BareDeclaration | FlowchartLabelOrigin.ImplicitEdgeRef;
}): FlowchartLabelOrigin {
  return input.label ? FlowchartLabelOrigin.ExplicitLabel : input.fallbackOrigin;
}

function buildUndescribedNodeReference(input: {
  parts: NodeExpressionParts;
  origin: FlowchartLabelOrigin.BareDeclaration | FlowchartLabelOrigin.ImplicitEdgeRef;
}): ParsedNodeReference {
  return buildNodeReference({
    id: input.parts.id,
    label: null,
    labelOrigin: input.origin,
    shape: null,
    classNames: input.parts.classNames,
  });
}

function parseExtendedNodeReference(input: {
  parts: NodeExpressionParts;
  origin: FlowchartLabelOrigin.BareDeclaration | FlowchartLabelOrigin.ImplicitEdgeRef;
}): ParsedNodeReference | null {
  const extendedShape = parseExtendedShapeDescriptor(input.parts.descriptor);
  return extendedShape
    ? buildNodeReference({
        id: input.parts.id,
        label: extendedShape.label,
        labelOrigin: labelOriginForLabel({
          label: extendedShape.label,
          fallbackOrigin: input.origin,
        }),
        shape: extendedShape.shape,
        classNames: input.parts.classNames,
      })
    : null;
}

function findNodeShapePatternMatch(descriptor: string): NodeShapePatternMatch | null {
  const matches = NODE_SHAPE_PATTERNS.map((pattern) => ({
    pattern,
    match: descriptor.match(pattern.regex),
  }));
  const matched = matches.find((candidate) => candidate.match);
  return matched?.match ? { pattern: matched.pattern, match: matched.match } : null;
}

function parseShapedNodeReference(input: {
  parts: NodeExpressionParts;
  origin: FlowchartLabelOrigin.BareDeclaration | FlowchartLabelOrigin.ImplicitEdgeRef;
}): ParsedNodeReference | null {
  const matched = findNodeShapePatternMatch(input.parts.descriptor);
  if (!matched) {
    return null;
  }

  const label = toNullableLabel(getMatchGroup({ match: matched.match, index: 1 }) ?? '');
  return buildNodeReference({
    id: input.parts.id,
    label,
    labelOrigin: labelOriginForLabel({ label, fallbackOrigin: input.origin }),
    shape: matched.pattern.shape,
    classNames: input.parts.classNames,
  });
}

export function parseNodeExpression(input: {
  expression: string;
  origin: FlowchartLabelOrigin.BareDeclaration | FlowchartLabelOrigin.ImplicitEdgeRef;
}): ParsedNodeReference | null {
  const parts = readNodeExpressionParts(input);
  if (!parts) {
    return null;
  }

  if (!parts.descriptor) {
    return buildUndescribedNodeReference({ parts, origin: input.origin });
  }

  return (
    parseExtendedNodeReference({ parts, origin: input.origin }) ??
    parseShapedNodeReference({ parts, origin: input.origin })
  );
}
